import React from "react";
import { Phone, Mail, Calendar, MapPin, Clock, MessageCircle } from "lucide-react";
import { SectionLabel, HighlightText } from "@/components/Bits";
import ContactForm from "@/components/ContactForm";
import { CALENDLY_URL, PHONE_DISPLAY, EMAIL, WHATSAPP_URL, WHATSAPP_DISPLAY } from "@/lib/api";

export default function Contact() {
  return (
    <>
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 pt-10 lg:pt-16">
        <SectionLabel testid="contact-label">Contact</SectionLabel>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-6">
          <div className="lg:col-span-8" data-reveal>
            <h1 className="font-display font-extrabold tracking-tight text-forest-600 text-5xl md:text-7xl lg:text-[88px] leading-[0.95]">
              Let's Start a
              <br />
              <HighlightText><span className="font-serif-italic font-normal">Conversation.</span></HighlightText>
            </h1>
          </div>
          <div className="lg:col-span-4 lg:pt-12" data-reveal>
            <p className="text-forest-400 text-[15px] leading-relaxed">
              Tell us what you need. We'll come back the same business day with a clear next step and usually a 15-minute discovery call with a real person on our team.
            </p>
          </div>
        </div>
      </section>

      {/* CONTACT BLOCK */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: contact methods */}
          <div className="lg:col-span-5 space-y-4">
            <a href="tel:+16465951666" data-testid="contact-call-card" className="aidify-card p-5 flex items-center gap-4 group">
              <div className="w-12 h-12 rounded-xl bg-sage-200 flex items-center justify-center"><Phone size={18} className="text-forest-600" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-forest-400/70">Call us</div>
                <div className="font-display font-bold text-forest-600 text-lg">{PHONE_DISPLAY}</div>
                <div className="text-xs text-forest-400">Mon-Fri · 8am-8pm ET</div>
              </div>
            </a>
            <a href={`mailto:${EMAIL}`} data-testid="contact-email-card" className="aidify-card p-5 flex items-center gap-4 group">
              <div className="w-12 h-12 rounded-xl bg-sage-200 flex items-center justify-center"><Mail size={18} className="text-forest-600" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-forest-400/70">Email</div>
                <div className="font-display font-bold text-forest-600 text-lg">{EMAIL}</div>
                <div className="text-xs text-forest-400">We reply on same business day</div>
              </div>
            </a>
            <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="contact-calendly-card" className="aidify-card p-5 flex items-center gap-4 group">
              <div className="w-12 h-12 rounded-xl bg-sage-200 flex items-center justify-center"><Calendar size={18} className="text-forest-600" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-forest-400/70">Book a 30-min call</div>
                <div className="font-display font-bold text-forest-600 text-lg">Conect for a Virtual Coffee</div>
                <div className="text-xs text-forest-400">Pick a slot that works for you</div>
              </div>
            </a>

            <div className="aidify-card-dark p-6">
              <div className="text-[11px] uppercase tracking-[0.2em] text-cream-100/60 mb-4">• Offices</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-cream-100 font-medium flex items-center gap-2"><MapPin size={14} /> USA</div>
                  <div className="mt-1 text-cream-100/70 text-sm">San Francisco, CA</div>
                </div>
                <div>
                  <div className="text-cream-100 font-medium flex items-center gap-2"><MapPin size={14} /> India</div>
                  <div className="mt-1 text-cream-100/70 text-sm">Delhi, IN<br/>Dehradun, IN</div>
                </div>
              </div>
              <div className="mt-5 pt-5 border-t border-cream-100/15 flex items-center gap-2 text-cream-100/80 text-sm">
                <Clock size={14} className="text-sage-200" /> Avg reply time: <strong className="text-cream-100">Under 4 hrs</strong>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7" data-reveal>
            <ContactForm testid="contact-page-form" />

            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" data-testid="whatsapp-cta" className="aidify-card-sage mt-5 p-5 flex items-center justify-between gap-4 hover:brightness-95 transition-all">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-forest-600 text-cream-100 flex items-center justify-center"><MessageCircle size={16} /></div>
                <div>
                  <div className="font-display font-bold text-forest-600">Prefer a faster route?</div>
                  <div className="text-xs text-forest-500">WhatsApp us on <strong>{WHATSAPP_DISPLAY}</strong> — most messages get a reply within the hour.</div>
                </div>
              </div>
              <span className="btn-primary text-sm">Open Chat <MessageCircle size={14} /></span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
